import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { CountdownToLaunch } from '../components/CountdownToLaunch';
import { HelpCircle, ChevronDown, ArrowRight, Wallet, Sparkles } from 'lucide-react';

const FAQS = [
    { key: 'whatIsB4P', cat: 'general' },
    { key: 'whyDate', cat: 'general' },
    { key: 'howBuy', cat: 'sale' },
    { key: 'priceTGE', cat: 'sale' },
    { key: 'maxAlloc', cat: 'sale' },
    { key: 'vesting', cat: 'sale' },
    { key: 'utility', cat: 'utility' },
    { key: 'staking', cat: 'utility' },
    { key: 'referral', cat: 'utility' },
    { key: 'safety', cat: 'safety' },
    { key: 'audit', cat: 'safety' },
    { key: 'rugProof', cat: 'safety' },
    { key: 'airdrop', cat: 'airdrop' },
    { key: 'whitelist', cat: 'airdrop' },
];

const CATEGORIES = [
    { id: 'general', labelKey: 'faq.catGeneral' },
    { id: 'sale', labelKey: 'faq.catSale' },
    { id: 'utility', labelKey: 'faq.catUtility' },
    { id: 'safety', labelKey: 'faq.catSafety' },
    { id: 'airdrop', labelKey: 'faq.catAirdrop' },
];

function FaqItem({ qKey, isOpen, onToggle }) {
    const { t } = useTranslation();
    return (
        <div
            className={`rounded-xl border transition-all ${
                isOpen ? 'bg-white/[0.06] border-[#FFD700]/30' : 'bg-white/[0.03] border-white/10 hover:border-white/20'
            }`}
            data-testid={`faq-item-${qKey}`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full text-left p-5 flex items-center justify-between gap-4"
                aria-expanded={isOpen}
            >
                <span className="font-semibold text-white text-base">{t(`faq.q_${qKey}`)}</span>
                <ChevronDown
                    className={`w-5 h-5 text-white/50 shrink-0 transition-transform ${isOpen ? 'rotate-180 text-[#FFD700]' : ''}`}
                />
            </button>
            {isOpen && (
                <div className="px-5 pb-5 -mt-2 text-white/75 leading-relaxed text-[15px]">
                    {t(`faq.a_${qKey}`)}
                </div>
            )}
        </div>
    );
}

export default function FAQPage() {
    const { t } = useTranslation();
    const [activeCat, setActiveCat] = useState('general');
    const [openItem, setOpenItem] = useState(null);

    const filtered = FAQS.filter(f => f.cat === activeCat);

    return (
        <div className="min-h-screen" data-testid="faq-page">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0066CC]/10 via-transparent to-[#FFD700]/10" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0066CC]/15 border border-[#0066CC]/30 mb-6">
                        <HelpCircle className="w-3.5 h-3.5 text-[#6FA8FF]" />
                        <span className="text-[#6FA8FF] text-xs font-bold uppercase tracking-widest">
                            {t('faq.heroBadge')}
                        </span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-4">
                        {t('faq.heroTitle1')} <span className="text-[#FFD700]">B4P</span>
                    </h1>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
                        {t('faq.heroDesc')}
                    </p>
                    <CountdownToLaunch compact />
                </div>
            </section>

            {/* FAQ Body */}
            <section className="py-16">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    {/* Category tabs */}
                    <div className="flex flex-wrap gap-2 justify-center mb-10">
                        {CATEGORIES.map((c) => (
                            <button
                                type="button"
                                key={c.id}
                                onClick={() => { setActiveCat(c.id); setOpenItem(null); }}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                                    activeCat === c.id
                                        ? 'bg-[#FFD700] text-[#001A4D] shadow-[0_0_20px_rgba(255,215,0,0.3)]'
                                        : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
                                }`}
                                data-testid={`faq-cat-${c.id}`}
                            >
                                {t(c.labelKey)}
                            </button>
                        ))}
                    </div>

                    {/* Questions */}
                    <div className="space-y-3">
                        {filtered.map((f) => (
                            <FaqItem
                                key={f.key}
                                qKey={f.key}
                                isOpen={openItem === f.key}
                                onToggle={() => setOpenItem(openItem === f.key ? null : f.key)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 border-t border-white/10 bg-gradient-to-br from-[#FFD700]/5 to-transparent">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <Sparkles className="w-10 h-10 text-[#FFD700] mx-auto mb-3" />
                    <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
                        {t('faq.ctaTitle')}
                    </h2>
                    <p className="text-white/60 mb-8">{t('faq.ctaDesc')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/b4p-launch">
                            <Button size="lg" className="bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold px-8 py-6" data-testid="faq-cta-buy">
                                <Wallet className="w-5 h-5 mr-2" />
                                {t('faq.ctaBuy')}
                            </Button>
                        </Link>
                        <Link to="/tokenomics">
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/5 px-8 py-6" data-testid="faq-cta-token">
                                {t('faq.ctaToken')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
